import { decodeVarint } from "./varintUtils";
import { bufferToPrettyHex } from "./hexUtils";

// 添加缓存机制
const decodeCache = new Map();
const CACHE_SIZE_LIMIT = 100;
const CACHE_CLEANUP_THRESHOLD = 80;

// 从Uint8Array读取32位大端整数
function readInt32BE(bytes, offset = 0) {
  const view = new DataView(bytes.buffer, bytes.byteOffset + offset, 4);
  return view.getInt32(0, false); // false for big-endian
}

class BufferReader {
  constructor(buffer) {
    this.buffer = buffer;
    this.offset = 0;
  }

  readVarInt() {
    const result = decodeVarint(this.buffer, this.offset);
    this.offset += result.length;

    return result.value;
  }

  readBuffer(length) {
    this.checkByte(length);
    const result = this.buffer.slice(this.offset, this.offset + length);
    this.offset += length;

    return result;
  }

  // gRPC has some additional header - remove it
  trySkipGrpcHeader() {
    const backupOffset = this.offset;

    if (this.buffer[this.offset] === 0 && this.leftBytes() >= 5) {
      this.offset++;
      const length = readInt32BE(this.buffer, this.offset);
      this.offset += 4;

      if (length > this.leftBytes()) {
        // Something is wrong, revert
        this.offset = backupOffset;
      }
    }
  }

  leftBytes() {
    return this.buffer.length - this.offset;
  }

  checkByte(length) {
    const bytesAvailable = this.leftBytes();
    if (length > bytesAvailable) {
      throw new Error(
        "Not enough bytes left. Requested: " +
          length +
          " left: " +
          bytesAvailable
      );
    }
  }

  checkpoint() {
    this.savedOffset = this.offset;
  }

  resetToCheckpoint() {
    this.offset = this.savedOffset;
  }
}

export const TYPES = {
  MSG_LEN_DELIMITER: -1,
  VARINT: 0,
  FIXED64: 1,
  LENDELIM: 2,
  FIXED32: 5
};

// 智能缓存清理函数
function cleanupDecodeCache() {
  const entries = Array.from(decodeCache.entries());
  // 按时间戳排序，保留最近使用的
  entries.sort((a, b) => b[1].timestamp - a[1].timestamp);
  
  // 保留前CACHE_SIZE_LIMIT个条目
  const toDelete = entries.slice(CACHE_SIZE_LIMIT);
  toDelete.forEach(([key]) => decodeCache.delete(key));
}

export function decodeProto(buffer, parseDelimited) {
  // 创建缓存键 - 使用bufferToPrettyHex替代buffer.toString('hex')
  const cacheKey = `${bufferToPrettyHex(buffer).replace(/\s/g, '')}-${parseDelimited}`;
  
  // 检查缓存
  if (decodeCache.has(cacheKey)) {
    const cached = decodeCache.get(cacheKey);
    cached.accessCount++;
    cached.timestamp = Date.now();
    return cached.result;
  }

  const reader = new BufferReader(buffer);
  const parts = [];

  reader.trySkipGrpcHeader();

  var protoBufMsgLength = 0;
  var protoBufMsgEnd = 0;

  try {
    while (reader.leftBytes() > 0) {
      reader.checkpoint();

      if (parseDelimited && protoBufMsgEnd === reader.offset) {
        const byteRange = [reader.offset];
        protoBufMsgLength = parseInt(reader.readVarInt().toString());
        protoBufMsgEnd = reader.offset + protoBufMsgLength;
        byteRange.push(reader.offset);
        parts.push({
          byteRange,
          index: -1,
          type: TYPES.MSG_LEN_DELIMITER,
          value: protoBufMsgLength
        });
      }

      const byteRange = [reader.offset];
      const indexType = parseInt(reader.readVarInt().toString());
      const type = indexType & 0b111;
      const index = indexType >> 3;
      
      // 验证字段号范围 (proto3 规范)
      if (index < 1 || index > 536870911) {
        console.log(new Error(`Invalid field number: ${index}. Must be between 1 and 536,870,911`));
        reader.resetToCheckpoint();
        break;
      }
      
      // 检查保留字段号范围
      if (index >= 19000 && index <= 19999) {
        console.log(new Error(`Reserved field number: ${index}. Field numbers 19,000-19,999 are reserved`));
        reader.resetToCheckpoint();
        break;
      }

      // 验证 wire type (proto3 规范)
      const validWireTypes = [TYPES.VARINT, TYPES.FIXED64, TYPES.LENDELIM, TYPES.FIXED32];
      if (!validWireTypes.includes(type)) {
        console.log(new Error(`Invalid wire type: ${type}. Valid types are 0(VARINT), 1(FIXED64), 2(LENDELIM), 5(FIXED32)`));
        reader.resetToCheckpoint();
        break;
      }

      let value;
      if (type === TYPES.VARINT) {
        value = reader.readVarInt().toString();
      } else if (type === TYPES.LENDELIM) {
        const length = parseInt(reader.readVarInt().toString());
        value = reader.readBuffer(length);
        
        // 检测是否为 packed repeated field
        if (isPackedRepeatedField(value)) {
          // 标记为 packed repeated field
          value._isPackedRepeated = true;
        }
      } else if (type === TYPES.FIXED32) {
        value = reader.readBuffer(4);
      } else if (type === TYPES.FIXED64) {
        value = reader.readBuffer(8);
      }
      byteRange.push(reader.offset);

      parts.push({
        byteRange,
        index,
        type,
        value
      });
    }
  } catch (err) {
    console.log(err);
    reader.resetToCheckpoint();
  }

  const result = {
    parts,
    leftOver: reader.readBuffer(reader.leftBytes())
  };

  // 缓存结果，添加时间戳和访问计数
  decodeCache.set(cacheKey, {
    result,
    timestamp: Date.now(),
    accessCount: 1
  });
  
  // 智能缓存清理
  if (decodeCache.size > CACHE_CLEANUP_THRESHOLD) {
    cleanupDecodeCache();
  }

  return result;
}

// 检测是否为 packed repeated field
function isPackedRepeatedField(buffer) {
  if (buffer.length === 0) return false;
  
  // 尝试解析为多个 varint 值
  // 这是一个启发式检测，检查是否所有字节都能被解析为有效的 varint
  try {
    let offset = 0;
    let varintCount = 0;
    
    while (offset < buffer.length) {
      const startOffset = offset;
      
      // 尝试解析一个 varint
      while (offset < buffer.length && buffer[offset] >= 0x80) {
        offset++;
      }
      if (offset >= buffer.length) return false;
      offset++; // 最后一个字节
      
      // 检查是否至少解析了一个完整的 varint
      if (offset > startOffset) {
        varintCount++;
      } else {
        return false;
      }
    }
    
    // 更严格的检测：必须是多个 varint 值，且每个值都相对较小
    // 避免将字符串误识别为 packed repeated
    if (varintCount < 2 || offset !== buffer.length) {
      return false;
    }
    
    // 额外的启发式检测：检查是否看起来像字符串
    // 如果所有字节都是可打印的 ASCII 字符，则可能是字符串
    const isPrintableAscii = Array.from(buffer).every(b => b >= 32 && b <= 126);
    if (isPrintableAscii) {
      return false; // 可能是字符串，不是 packed repeated
    }
    
    // 检查 varint 值的分布
    // 如果所有 varint 值都很小（单字节），且数量较多，则可能是 packed repeated
    const singleByteVarints = Array.from(buffer).filter(b => b < 0x80).length;
    const totalBytes = buffer.length;
    
    // 如果大部分字节都是单字节 varint，则可能是 packed repeated
    return singleByteVarints >= totalBytes * 0.8;
  } catch (e) {
    return false;
  }
}

export function typeToString(type, subType) {
  switch (type) {
    case TYPES.VARINT:
      return "varint";
    case TYPES.LENDELIM:
      return subType || "len_delim";
    case TYPES.FIXED32:
      return "fixed32";
    case TYPES.FIXED64:
      return "fixed64";
    case TYPES.MSG_LEN_DELIMITER:
      return "Message delimiter";
    default:
      return "unknown";
  }
}
